const layerId = (layer) => String(layer && layer.id != null ? layer.id : '');

const normalizedText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase();

/**
 * Identifica las capas censales auxiliares. Estas capas alimentan las
 * estadísticas de población, pero no representan infraestructura expuesta.
 */
export function isPopulationLayer(layer = {}) {
  const populationFlag = normalizedText(
    layer.cap_poblacion != null ? layer.cap_poblacion : layer.capPoblacion
  );
  if (populationFlag && !['no', 'false', '0', 'ninguno'].includes(populationFlag)) {
    return true;
  }

  return normalizedText(layer.category_name).includes('poblacion') ||
    normalizedText(layer.name).includes('poblacion');
}

/**
 * Cuenta filas del área total con la misma semántica del reporte. Para
 * procesos con buffers acumulativos usa solamente el radio exterior y no
 * deduplica por OBJECTID. Las capas poblacionales quedan fuera.
 */
export function summarizeInfrastructureByLayer(rawResults = [], graphType = '') {
  const results = (rawResults || []).filter(item => !isPopulationLayer(item.layer || {}));
  const cumulative = ['InfluenciaRadial', 'InfluenciaLineal'].includes(graphType);
  const numericRadius = value => value == null || value === '' ? null : Number(value);
  const radii = results
    .map(item => numericRadius(item.radius))
    .filter(radius => radius !== null && Number.isFinite(radius));
  const outerRadius = radii.length ? Math.max(...radii) : null;
  const counts = new Map();

  results.forEach(item => {
    const itemRadius = numericRadius(item.radius);
    if (cumulative && outerRadius !== null && itemRadius !== outerRadius) return;

    const layer = item.layer || {};
    const id = layerId(layer);
    const features = (item.layer_results &&
      (item.layer_results.features || item.layer_results.data)) || [];
    const current = counts.get(id) || {
      layer_name: layer.name || 'Capa sin nombre',
      features_count: 0,
    };
    current.features_count += features.length;
    counts.set(id, current);
  });

  return Array.from(counts.values()).filter(row => row.features_count > 0);
}

/**
 * Desglose exclusivo por radio ("Área de influencia"): el backend solo
 * calcula esto para InfluenciaRadial (_build_radial_graph); para
 * InfluenciaLineal colapsa directo al radio exterior y nunca lo expone.
 * Se reconstruye acá con la misma semántica — suma bruta de features por
 * radio (sin deduplicar, igual que el backend) y resta el radio anterior
 * para quedarse con el incremento neto de cada anillo — para que aplique
 * a ambos tipos de geoproceso acumulativo.
 */
export function summarizeByRadiusExclusive(rawResults = [], graphType = '') {
  if (!['InfluenciaRadial', 'InfluenciaLineal'].includes(graphType)) return [];

  const results = (rawResults || []).filter(item => !isPopulationLayer(item.layer || {}));
  const byRadius = new Map();

  results.forEach(item => {
    if (item.radius == null) return;
    const radius = Number(item.radius);
    if (!Number.isFinite(radius)) return;
    const features = (item.layer_results &&
      (item.layer_results.features || item.layer_results.data)) || [];
    byRadius.set(radius, (byRadius.get(radius) || 0) + features.length);
  });

  const sorted = Array.from(byRadius.entries()).sort((a, b) => a[0] - b[0]);
  let previous = 0;
  const exclusive = sorted.map(([radius, cumulative]) => {
    const features_count = cumulative - previous;
    previous = cumulative;
    return { radius, features_count };
  });

  const total = exclusive.reduce((sum, row) => sum + row.features_count, 0) || 1;
  return exclusive.map(row => ({
    ...row,
    percentage: Math.round((row.features_count / total) * 10000) / 100,
  }));
}

/**
 * Conserva únicamente capas que siguen marcadas como vulnerables en el
 * catálogo obtenido al iniciar el geoproceso.
 */
export function filterVulnerableLayers(candidates = [], vulnerableLayers = []) {
  const vulnerableIds = new Set(vulnerableLayers.map(layerId).filter(Boolean));
  return (candidates || []).filter(layer => vulnerableIds.has(layerId(layer)));
}

/**
 * Catálogo mínimo y autocontenido que acompaña al resultado hasta el reporte.
 * Los metadatos del endpoint vulnerable son autoritativos; la capa procesada
 * queda como respaldo para resultados de instalaciones antiguas.
 */
export function buildVulnerableCatalog(processedLayers = [], vulnerableLayers = []) {
  const currentById = new Map(
    (vulnerableLayers || []).map(layer => [layerId(layer), layer])
  );

  return (processedLayers || []).map(layer => {
    const current = currentById.get(layerId(layer)) || layer;
    return {
      id: current.id != null ? current.id : layer.id,
      id_category: current.id_category != null
        ? current.id_category
        : layer.id_category,
      category_name: current.category_name || layer.category_name || '',
      cap_poblacion: current.cap_poblacion != null
        ? current.cap_poblacion
        : (layer.cap_poblacion || layer.capPoblacion || ''),
      report_icon_svg: current.report_icon_svg || layer.report_icon_svg || '',
    };
  });
}

/** Agrupa el catálogo vigente para el selector sin depender de una lista fija. */
export function groupVulnerableLayers(vulnerableLayers = [], knownCategories = []) {
  const knownNames = new Map(
    (knownCategories || []).map(category => [String(category.id), category.name])
  );
  const groups = new Map();

  (vulnerableLayers || []).forEach(layer => {
    const categoryId = String(layer.id_category != null ? layer.id_category : 'vulnerable');
    if (!groups.has(categoryId)) {
      groups.set(categoryId, {
        id: categoryId,
        name: layer.category_name || knownNames.get(categoryId) || 'Capas vulnerables',
        layers: [],
      });
    }
    groups.get(categoryId).layers.push(layer);
  });

  return Array.from(groups.values());
}
